import { compressImage } from '@/features/atendimento/lib/compress-image';
import {
  FileText,
  Image as ImageIcon,
  Loader2,
  Music,
  Plus,
  Search,
  Trash2,
  Upload,
  Video,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { type LibraryMedia, type MediaType, getMediaUrlById } from './api';
import { useMediaLibrary, useUploadLibrary } from './hooks';

/**
 * Restringe o picker a UM tipo de arquivo (ex.: cabeçalho de template só aceita
 * imagem JPG/PNG até 5 MB). Sem restrição, o picker aceita qualquer mídia
 * (uso do chatbot).
 */
export interface RestricaoDeMidia {
  tipo: MediaType;
  /** `accept` do seletor de arquivo do computador. */
  aceita: string;
  limiteBytes: number;
  /** "imagem" / "vídeo" / "documento" — nas mensagens de erro. */
  nome: string;
}

/** Limites da Meta pro cabeçalho de template HSM (iguais aos do Composer). */
export function restricaoDoCabecalho(formato: 'image' | 'video' | 'document'): RestricaoDeMidia {
  if (formato === 'image') {
    return {
      tipo: 'image',
      aceita: 'image/jpeg,image/png',
      limiteBytes: 5 * 1024 * 1024,
      nome: 'imagem',
    };
  }
  if (formato === 'video') {
    return {
      tipo: 'video',
      aceita: 'video/mp4,video/3gpp',
      limiteBytes: 16 * 1024 * 1024,
      nome: 'vídeo',
    };
  }
  return {
    tipo: 'document',
    aceita: 'application/pdf,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt',
    limiteBytes: 100 * 1024 * 1024,
    nome: 'documento',
  };
}

/** O arquivo é do tipo pedido? Documento = qualquer coisa que não seja imagem/vídeo/áudio. */
function arquivoServe(restricao: RestricaoDeMidia, mime: string): boolean {
  const m = mime.toLowerCase();
  if (restricao.tipo === 'document') {
    return !m.startsWith('image/') && !m.startsWith('video/') && !m.startsWith('audio/');
  }
  return m.startsWith(`${restricao.tipo}/`);
}

/**
 * Picker da galeria — abre lista buscável, mostra preview e seleciona.
 * Botão "+" sobe arquivo direto pra galeria. Se já tem mídia escolhida,
 * mostra card resumido + botão "Trocar".
 */
export function MediaPicker({
  selectedId,
  selectedLabel,
  selectedMime,
  onSelect,
  onClear,
  restricao,
}: {
  selectedId: number | undefined;
  selectedLabel: string | undefined;
  selectedMime: string | undefined;
  onSelect: (m: LibraryMedia) => void;
  onClear: () => void;
  restricao?: RestricaoDeMidia;
}) {
  const [open, setOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  // Quem só tem o id (ex.: rascunho de campanha) não sabe o tipo — vem da busca.
  const [mimeBuscado, setMimeBuscado] = useState<string | null>(null);
  const mime = selectedMime ?? mimeBuscado ?? undefined;

  // Busca preview da mídia selecionada (só image — outros mostram ícone).
  useEffect(() => {
    setMimeBuscado(null);
    if (!selectedId || (selectedMime && !selectedMime.startsWith('image/'))) {
      setPreviewUrl(null);
      return;
    }
    let cancelled = false;
    getMediaUrlById(selectedId)
      .then(({ url, mime_type }) => {
        if (cancelled) return;
        setMimeBuscado(mime_type);
        setPreviewUrl((selectedMime ?? mime_type).startsWith('image/') ? url : null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [selectedId, selectedMime]);

  const Icon = !mime
    ? FileText
    : mime.startsWith('image/')
      ? ImageIcon
      : mime.startsWith('audio/')
        ? Music
        : mime.startsWith('video/')
          ? Video
          : FileText;

  if (selectedId) {
    return (
      <>
        <div className="flex items-center gap-2 rounded border border-n-200 bg-n-50 px-2 py-2">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded bg-n-0">
            {previewUrl ? (
              // biome-ignore lint/a11y/useAltText: thumb
              <img
                src={previewUrl}
                alt={selectedLabel ?? ''}
                className="h-full w-full object-cover"
              />
            ) : (
              <Icon size={20} className="text-brand-600" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate font-semibold text-[12px] text-n-900">
              {selectedLabel ?? `Mídia #${selectedId}`}
            </div>
            <div className="truncate text-[10px] text-n-500">{mime ?? '—'}</div>
          </div>
          {/* Escrito, não ícone: a lupa parecia "pesquisar" (dono, 20/out/2026). */}
          <button
            type="button"
            onClick={() => setOpen(true)}
            title="Trocar mídia"
            className="flex h-7 flex-shrink-0 items-center rounded px-2 font-semibold text-[11px] text-brand-600 hover:bg-brand-50"
          >
            Trocar
          </button>
          <button
            type="button"
            onClick={onClear}
            title="Remover"
            className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded text-n-500 hover:bg-danger-100 hover:text-danger-600"
          >
            <Trash2 size={14} />
          </button>
        </div>
        {open && (
          <PickerModal
            onClose={() => setOpen(false)}
            onSelect={onSelect}
            {...(restricao && { restricao })}
          />
        )}
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center justify-center gap-2 rounded border border-n-300 border-dashed bg-n-0 px-3 py-4 text-[12px] text-n-600 hover:border-brand-400 hover:bg-brand-50"
      >
        <Search size={14} />
        Escolher da galeria
      </button>
      {open && (
        <PickerModal
          onClose={() => setOpen(false)}
          onSelect={onSelect}
          {...(restricao && { restricao })}
        />
      )}
    </>
  );
}

function PickerModal({
  onClose,
  onSelect,
  restricao,
}: {
  onClose: () => void;
  onSelect: (m: LibraryMedia) => void;
  restricao?: RestricaoDeMidia;
}) {
  const [q, setQ] = useState('');
  const [type, setType] = useState<MediaType | 'all'>(restricao?.tipo ?? 'all');
  const [showUpload, setShowUpload] = useState(false);
  const params = useMemo(
    () => ({
      ...(q.trim() && { q: q.trim() }),
      ...(type !== 'all' && { type }),
    }),
    [q, type],
  );
  const list = useMediaLibrary(params);
  const items = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);

  const sentinelRef = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    if (!list.hasNextPage || list.isFetchingNextPage) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) list.fetchNextPage();
      },
      { rootMargin: '200px' },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [list.hasNextPage, list.isFetchingNextPage, list.fetchNextPage, items.length]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        // Esc fecha SÓ a galeria — sem isso subia até o modal de fora (ex.:
        // Novo atendimento), que fecha e limpa o formulário inteiro.
        if (e.key === 'Escape') {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="flex max-h-[80vh] w-[640px] max-w-[calc(100vw-32px)] flex-col rounded-md bg-n-0 p-4">
        <header className="mb-3 flex items-center justify-between">
          <h3 className="font-bold text-[16px]">Galeria de Mídia</h3>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded text-n-500 hover:bg-n-100"
          >
            <X size={14} />
          </button>
        </header>

        <div className="mb-3 flex gap-2">
          <div className="relative flex-1">
            <Search size={14} className="-translate-y-1/2 absolute top-1/2 left-2 text-n-400" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar…"
              className="h-9 w-full rounded border border-n-200 bg-n-0 pr-3 pl-7 text-[13px] focus:border-brand-500 focus:outline-none"
            />
          </div>
          {/* Com restrição o tipo é fixo (ex.: cabeçalho de imagem). */}
          {!restricao && (
            <select
              value={type}
              onChange={(e) => setType(e.target.value as MediaType | 'all')}
              className="h-9 rounded border border-n-200 bg-n-0 px-2 text-[13px] focus:border-brand-500 focus:outline-none"
            >
              <option value="all">Todos</option>
              <option value="image">Imagens</option>
              <option value="audio">Áudios</option>
              <option value="video">Vídeos</option>
              <option value="document">Documentos</option>
            </select>
          )}
          <button
            type="button"
            onClick={() => setShowUpload(true)}
            className="flex h-9 items-center gap-1.5 rounded bg-brand-500 px-3 font-semibold text-[12px] text-white hover:bg-brand-600"
          >
            <Plus size={14} /> Enviar
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto rounded border border-n-100 bg-n-50">
          {list.isLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 size={18} className="animate-spin text-n-400" />
            </div>
          ) : items.length === 0 ? (
            <p className="py-10 text-center text-[13px] text-n-500">
              {q || type !== 'all' ? 'Nenhuma mídia encontrada.' : 'Galeria vazia.'}
            </p>
          ) : (
            <ul className="divide-y divide-n-200 bg-n-0">
              {items.map((m) => (
                <PickerRow key={m.id} media={m} onSelect={() => onSelect(m)} onClose={onClose} />
              ))}
              {list.hasNextPage && (
                <li
                  ref={sentinelRef}
                  className="flex items-center justify-center py-3 text-[11px] text-n-400"
                >
                  {list.isFetchingNextPage ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    'Carregar mais…'
                  )}
                </li>
              )}
            </ul>
          )}
        </div>

        {showUpload && (
          <PickerUploadModal
            onClose={() => setShowUpload(false)}
            onUploaded={(m) => {
              setShowUpload(false);
              onSelect(m);
              // Com restrição (cabeçalho), subir já é escolher — fecha a galeria.
              if (restricao) onClose();
            }}
            {...(restricao && { restricao })}
          />
        )}
      </div>
    </div>
  );
}

function PickerRow({
  media,
  onSelect,
  onClose,
}: {
  media: LibraryMedia;
  onSelect: () => void;
  onClose: () => void;
}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    if (media.type !== 'image') return;
    let cancelled = false;
    getMediaUrlById(media.id)
      .then(({ url }) => {
        if (!cancelled) setPreviewUrl(url);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [media.id, media.type]);

  const Icon =
    media.type === 'image'
      ? ImageIcon
      : media.type === 'audio'
        ? Music
        : media.type === 'video'
          ? Video
          : FileText;

  return (
    <li>
      <button
        type="button"
        onClick={() => {
          onSelect();
          onClose();
        }}
        className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-brand-50"
      >
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded bg-n-100">
          {previewUrl ? (
            // biome-ignore lint/a11y/useAltText: thumb
            <img src={previewUrl} alt={media.label} className="h-full w-full object-cover" />
          ) : (
            <Icon size={18} className="text-n-500" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate font-semibold text-[13px] text-n-900">{media.label}</div>
          <div className="truncate text-[11px] text-n-500">
            {media.original_name} · {media.mime_type}
          </div>
        </div>
      </button>
    </li>
  );
}

function PickerUploadModal({
  onClose,
  onUploaded,
  restricao,
}: {
  onClose: () => void;
  onUploaded: (m: LibraryMedia) => void;
  restricao?: RestricaoDeMidia;
}) {
  const [label, setLabel] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const uploadMut = useUploadLibrary();

  useEffect(() => {
    if (file && !label) {
      setLabel(file.name.replace(/\.[^.]+$/, ''));
    }
  }, [file, label]);

  const handleSubmit = async (): Promise<void> => {
    if (!file || !label.trim()) return;
    if (restricao && !arquivoServe(restricao, file.type)) {
      setError(`Escolha ${restricao.tipo === 'image' ? 'uma' : 'um'} ${restricao.nome}.`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      let blob: Blob = file;
      let filename = file.name;
      if (file.type.startsWith('image/')) {
        try {
          const compressed = await compressImage(file, { forceJpeg: true });
          if (compressed.blob !== file) {
            blob = compressed.blob;
            if (compressed.blob.type === 'image/jpeg' && !/\.jpe?g$/i.test(filename)) {
              filename = filename.replace(/\.[^.]+$/, '') + '.jpg';
            }
          }
        } catch (err) {
          console.warn('image compression failed', err);
        }
      }
      // Imagem já foi comprimida acima; o limite vale pro que vai subir de fato.
      if (restricao && blob.size > restricao.limiteBytes) {
        const mb = Math.round(restricao.limiteBytes / 1024 / 1024);
        setError(`O WhatsApp aceita ${restricao.nome} de até ${mb} MB.`);
        return;
      }
      const result = await uploadMut.mutateAsync({ file: blob, label: label.trim(), filename });
      onUploaded(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha no upload');
    } finally {
      setBusy(false);
    }
  };

  const fileRef = useRef<HTMLInputElement | null>(null);
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
      onKeyDown={(e) => {
        // Esc fecha só esta janela (não a galeria nem o modal de fora).
        if (e.key === 'Escape') {
          e.stopPropagation();
          if (!busy) onClose();
        }
      }}
    >
      <div className="w-[480px] max-w-full rounded-lg bg-n-0 p-6 shadow-lg">
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-[16px] text-n-900">Enviar arquivo</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="flex h-8 w-8 items-center justify-center rounded-md text-n-500 hover:bg-n-100 disabled:opacity-50"
            aria-label="Fechar"
          >
            <X size={16} />
          </button>
        </div>

        <div className="mt-5 flex flex-col gap-3.5">
          <label className="flex flex-col gap-1">
            <span className="font-semibold text-[12px] text-n-700">Nome</span>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Ex: logo-empresa"
              maxLength={120}
              autoFocus
              className="block w-full rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] text-n-900 outline-none focus:border-brand-500"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-semibold text-[12px] text-n-700">Arquivo</span>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="flex items-center justify-between rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] text-left hover:border-brand-300"
            >
              <span className={file ? 'text-n-900' : 'text-n-400'}>
                {file ? file.name : 'Selecionar arquivo do computador…'}
              </span>
              <Upload size={14} className="text-n-500" />
            </button>
            <input
              ref={fileRef}
              type="file"
              accept={
                restricao?.aceita ?? 'image/*,audio/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv'
              }
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="hidden"
            />
          </label>
        </div>

        {error && (
          <div className="mt-3 rounded-md bg-danger-50 px-3 py-2 text-[12px] text-danger-700">
            {error}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md px-3 py-1.5 font-bold text-[11px] uppercase tracking-[0.05em] text-n-700 hover:bg-n-100 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={busy || !file || !label.trim()}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-500 px-3 py-1.5 font-bold text-[11px] uppercase tracking-[0.05em] text-white shadow-xs hover:bg-brand-600 disabled:bg-brand-300"
          >
            {busy && <Loader2 size={12} className="animate-spin" />}
            Enviar
          </button>
        </div>
      </div>
    </div>
  );
}
